// Jixxeo Walkie Talkies realtime engine: WS signaling + WebRTC audio mesh + PTT floor + voice effects.
const ICE = [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }];

function buildEffect(ctx, src, effect) {
  const dest = ctx.createMediaStreamDestination();
  let node = src;
  const chain = (n) => { node.connect(n); node = n; return n; };
  if (effect === "fx_radio") {
    const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 500; chain(hp);
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 3000; chain(lp);
    const ws = ctx.createWaveShaper(); const c = new Float32Array(256);
    for (let i = 0; i < 256; i++) { const x = (i / 128) - 1; c[i] = Math.tanh(x * 3); }
    ws.curve = c; chain(ws);
  } else if (effect === "fx_robot") {
    const g = ctx.createGain(); g.gain.value = 0; const osc = ctx.createOscillator(); osc.frequency.value = 55;
    osc.connect(g.gain); osc.start(); chain(g);
  } else if (effect === "fx_echo") {
    const d = ctx.createDelay(); d.delayTime.value = 0.22; const fb = ctx.createGain(); fb.gain.value = 0.35;
    d.connect(fb); fb.connect(d); node.connect(d); d.connect(dest);
  } else if (effect === "fx_deep") {
    const ls = ctx.createBiquadFilter(); ls.type = "lowshelf"; ls.frequency.value = 220; ls.gain.value = 10; chain(ls);
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 1800; chain(lp);
  }
  node.connect(dest);
  return dest.stream.getAudioTracks()[0];
}

export class WalkieEngine {
  constructor(emit) {
    this.emit = emit;
    this.ws = null; this.room = null; this.me = null;
    this.peers = new Map(); this.quality = {};
    this.track = null; this.mic = null; this.ctx = null;
    this.retry = 0; this.closed = false; this.speaker = null;
  }

  connect() {
    this.closed = false;
    const url = window.location.origin.replace(/^http/, "ws") + "/api/ws/walkie";
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => { this.retry = 0; this.emit("status", "online"); if (this.room) this.send({ t: "join", channel: this.room }); };
    ws.onmessage = (e) => { try { this.onMsg(JSON.parse(e.data)); } catch {} };
    ws.onclose = () => {
      this.emit("status", "offline");
      this.peers.forEach((p) => p.pc.close()); this.peers.clear(); this.emit("peers", []);
      if (this.closed) return;
      const wait = Math.min(15000, 1000 * 2 ** this.retry++);
      this.emit("status", "reconnecting");
      setTimeout(() => !this.closed && this.connect(), wait);
    };
    clearInterval(this.ping); this.ping = setInterval(() => this.send({ t: "ping" }), 20000);
    clearInterval(this.statsTimer); this.statsTimer = setInterval(() => this.measure(), 5000);
  }

  send(m) { if (this.ws && this.ws.readyState === 1) this.ws.send(JSON.stringify(m)); }

  async enableMic(effect = "none") {
    if (this.track) return true;
    try {
      this.mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = this.ctx.createMediaStreamSource(this.mic);
      this.track = effect && effect !== "none" ? buildEffect(this.ctx, src, effect) : this.mic.getAudioTracks()[0];
      this.track.enabled = false;
      return true;
    } catch { return false; }
  }

  join(cid) {
    if (this.room && this.room !== cid) this.leave();
    this.room = cid;
    this.send({ t: "join", channel: cid });
    if ("mediaSession" in navigator) {
      try { navigator.mediaSession.metadata = new window.MediaMetadata({ title: "Jixxeo Walkie Talkies", artist: "Live channel" }); } catch {}
    }
  }

  leave() {
    this.pttStop();
    this.send({ t: "leave" });
    this.peers.forEach((p) => { p.pc.close(); p.audio.remove(); }); this.peers.clear();
    this.room = null; this.emit("peers", []);
  }

  peer(uid, initiator) {
    if (this.peers.has(uid)) return this.peers.get(uid);
    const pc = new RTCPeerConnection({ iceServers: ICE });
    const audio = document.createElement("audio"); audio.autoplay = true; audio.playsInline = true;
    document.body.appendChild(audio);
    if (this.track) pc.addTrack(this.track, new MediaStream([this.track]));
    else pc.addTransceiver("audio", { direction: "recvonly" });
    pc.ontrack = (e) => { audio.srcObject = e.streams[0] || new MediaStream([e.track]); audio.play().catch(() => this.emit("autoplay_blocked")); };
    pc.onicecandidate = (e) => e.candidate && this.send({ t: "signal", to: uid, data: { candidate: e.candidate } });
    pc.onconnectionstatechange = () => this.emit("peers", this.peerList());
    const p = { pc, audio };
    this.peers.set(uid, p);
    if (initiator) {
      pc.createOffer().then((o) => pc.setLocalDescription(o)).then(() => this.send({ t: "signal", to: uid, data: { sdp: pc.localDescription } }));
    }
    return p;
  }

  peerList() { return [...this.peers.entries()].map(([uid, p]) => ({ uid, state: p.pc.connectionState, quality: this.quality[uid] })); }

  async onSignal(from, data) {
    const { pc } = this.peer(from, false);
    try {
      if (data.sdp) {
        await pc.setRemoteDescription(data.sdp);
        if (data.sdp.type === "offer") {
          const a = await pc.createAnswer(); await pc.setLocalDescription(a);
          this.send({ t: "signal", to: from, data: { sdp: pc.localDescription } });
        }
      } else if (data.candidate) await pc.addIceCandidate(data.candidate);
    } catch {}
  }

  onMsg(m) {
    switch (m.t) {
      case "hello": this.me = m.user_id; break;
      case "room": m.peers.forEach((uid) => this.peer(uid, true)); this.speaker = m.speaker; this.emit("speaking", m.speaker); this.emit("peers", this.peerList()); break;
      case "peer_join": this.emit("peer_join", m); break;
      case "peer_leave": { const p = this.peers.get(m.user_id); if (p) { p.pc.close(); p.audio.remove(); this.peers.delete(m.user_id); } this.emit("peers", this.peerList()); break; }
      case "signal": this.onSignal(m.from, m.data); break;
      case "speaking":
        this.speaker = m.user_id;
        if (m.user_id !== this.me && this.track) this.track.enabled = false;
        if (m.ended === this.me && this.track) this.track.enabled = false;
        this.emit("speaking", m.user_id, m.reason); break;
      case "busy": if (this.track) this.track.enabled = false; this.emit("busy", m.speaker); break;
      case "quality": this.quality[m.user_id] = m.value; this.emit("peers", this.peerList()); break;
      default: this.emit(m.t, m);
    }
  }

  pttStart() {
    if (!this.room) return false;
    if (this.track) this.track.enabled = true;
    if (this.ctx && this.ctx.state === "suspended") this.ctx.resume();
    this.send({ t: "ptt_start" });
    return true;
  }

  pttStop() { if (this.track) this.track.enabled = false; if (this.room) this.send({ t: "ptt_stop" }); }

  async measure() {
    let worst = null;
    for (const [, p] of this.peers) {
      try {
        const stats = await p.pc.getStats();
        stats.forEach((r) => {
          if (r.type === "candidate-pair" && r.state === "succeeded" && r.currentRoundTripTime != null) {
            const rtt = r.currentRoundTripTime * 1000;
            const q = rtt < 150 ? "good" : rtt < 400 ? "fair" : "poor";
            if (!worst || ["good", "fair", "poor"].indexOf(q) > ["good", "fair", "poor"].indexOf(worst)) worst = q;
          }
        });
      } catch {}
    }
    if (worst) { this.emit("my_quality", worst); this.send({ t: "quality", value: worst }); }
  }

  destroy() {
    this.closed = true; this.leave();
    clearInterval(this.ping); clearInterval(this.statsTimer);
    try { this.ws && this.ws.close(); } catch {}
    if (this.mic) this.mic.getTracks().forEach((t) => t.stop());
    try { this.ctx && this.ctx.close(); } catch {}
  }
}

// ---- Offline voice-message outbox (IndexedDB) ----
const DB = "jixxeo-walkie"; const STORE = "outbox";
function idb() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: "id" });
    r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error);
  });
}
export async function outboxAdd(item) {
  const db = await idb();
  return new Promise((res) => { const tx = db.transaction(STORE, "readwrite"); tx.objectStore(STORE).put(item); tx.oncomplete = res; });
}
export async function outboxAll() {
  const db = await idb();
  return new Promise((res) => { const q = db.transaction(STORE).objectStore(STORE).getAll(); q.onsuccess = () => res(q.result || []); q.onerror = () => res([]); });
}
export async function outboxDelete(id) {
  const db = await idb();
  return new Promise((res) => { const tx = db.transaction(STORE, "readwrite"); tx.objectStore(STORE).delete(id); tx.oncomplete = res; });
}
